import type { Application, DestroyOptions, Renderer } from 'pixi.js';

/**
 * Every Pixi Application / renderer teardown in the app goes through this module
 * (a test enforces it: nothing else may call `.destroy(` on an app or renderer).
 *
 * Pixi 8 releases `GlobalResourceRegistry` (the batch pools and shared geometry
 * every live renderer draws with) when a renderer is destroyed with `true` or
 * with `releaseGlobalResources`. The game, the chest stage, the arrival and the
 * rank-up ceremony run side by side, so tearing one down must never wipe the
 * others' shared state: a later draw on a survivor then reads a null
 * `geometry` / `_resolution`. Mirrors Blockari's render/pixi-destroy.ts
 * (8cbb9fc).
 */
export const KEEP_GLOBALS = { removeView: true, releaseGlobalResources: false } as const;

/** What a ticker needs for us to silence it: Pixi's `Ticker` has `stop()`. */
interface Stoppable {
  stop(): void;
}

/**
 * Destroy an Application: its own ticker is stopped first (a frame must not land
 * between the renderer going and the ticker's own destroy), the canvas is
 * removed, and the global resources are kept.
 */
export function destroyApp(app: Application, options: DestroyOptions = false): void {
  (app.ticker as Stoppable | undefined)?.stop();
  app.destroy(KEEP_GLOBALS, options);
}

/** Destroy a standalone renderer (a stage that owns its canvas), keeping the global resources. */
export function destroyRenderer(renderer: Renderer): void {
  renderer.destroy(KEEP_GLOBALS);
}
